import { STATUT_LABELS } from './devisUtils';
import { statutsDeLEtape } from './etapes';

/**
 * Facettes de filtre proposées par chaque page, sous la forme attendue par
 * StatutsBarre : { cle, label, classe }.
 *
 * Une page ne propose que le vocabulaire qui la concerne. Les clés sont
 * exactement celles que le serveur renvoie dans `compteurs` et accepte en
 * paramètre de filtre — toute divergence rendrait une pastille inopérante.
 *
 * La couleur code la nature de la facette, pas son ancienneté :
 *   bleu = à faire, ambre = en attente d'un tiers, violet = document produit,
 *   vert = acquis, rouge = perdu ou urgent, gris = clos.
 */

/** Étape 1 — statuts de traitement de la demande. */
export const FACETTES_DEMANDE = statutsDeLEtape('demande').map((cle) => ({
  cle,
  label: STATUT_LABELS[cle] || cle,
  classe: `statut-${cle}`,
}));

/**
 * Origine d'un devis, d'un contrat ou d'un dossier (migration 106) : « interne »
 * est généré par l'application, « externe » a été établi hors de l'application
 * puis téléversé. Une dimension à part, qui se combine avec le statut : les
 * clés sont celles du serveur (paramètre `origine` et `compteursOrigine`).
 */
export const FACETTES_ORIGINE = [
  { cle: 'interne', label: 'Interne', classe: 'facette-indigo' },
  { cle: 'externe', label: 'Externe', classe: 'facette-violet' },
];

/**
 * Étape 2 — suivi d'un devis parti au client.
 *
 * Ce ne sont pas les valeurs de `statut_devis` : la page ne liste que les
 * devis déjà envoyés, et ce qui compte alors est la réponse du client et
 * l'échéance de relance. Le serveur calcule ces cinq cas, exclusifs entre eux
 * (cf. devisService.listDevis).
 */
export const FACETTES_DEVIS = [
  // Validé mais jamais parti : l'email a échoué. Le passage quotidien
  // retente pendant quelques jours, cette pastille le rend visible entre-temps.
  { cle: 'a_envoyer', label: 'À envoyer', classe: 'facette-rouge' },
  { cle: 'envoye', label: 'Devis envoyé', classe: 'facette-indigo' },
  { cle: 'a_relancer', label: 'Devis à relancer', classe: 'facette-rouge' },
  { cle: 'relance', label: 'Devis relancé', classe: 'facette-ambre' },
  { cle: 'accepte', label: 'Devis accepté et signé', classe: 'facette-vert' },
  { cle: 'sans_suite', label: 'Sans suite', classe: 'facette-gris' },
];

/**
 * Étape 3 — avancement du contrat, déduit des trois signatures (devis,
 * organisme, client).
 *
 * « À valider et envoyer » couvre tout ce qui n'est pas encore parti, y
 * compris un contrat validé dont l'email a échoué. La bascule vers
 * « envoyé » se lit sur `contrats.date_envoi` (migration 021), renseigné
 * après un envoi réussi et lui seul.
 */
export const FACETTES_CONTRAT = [
  { cle: 'a_envoyer', label: 'Contrat à envoyer', classe: 'facette-ambre' },
  { cle: 'envoye', label: 'Contrat envoyé', classe: 'facette-ambre' },
  { cle: 'contrat_signe', label: 'Contrat accepté et signé', classe: 'facette-vert' },
];

/**
 * Deuxième rangée de la page Contrat : les journées sont-elles posées ?
 *
 * Dimension indépendante des signatures depuis que l'envoi du contrat ne
 * l'exige plus. Un contrat peut être signé sans être planifié, et planifié
 * sans être signé — les deux rangées se combinent, comme sur la page Rappel.
 */
export const FACETTES_PLANIFICATION = [
  { cle: 'a_planifier', label: 'À planifier', classe: 'facette-rouge' },
  { cle: 'planifie', label: 'Planifié', classe: 'facette-vert' },
];

/**
 * Dimension d'envoi, indépendante des facettes de séance ou d'échéance : un
 * document peut rester à envoyer quelle que soit la date de sa séance. Les
 * deux rangées de pastilles se combinent.
 *
 * `date_envoi` / `rappel_envoye_le` (migration 022) portent le témoin, posé
 * après un envoi réussi et lui seul. La convocation part depuis la page ou la
 * planification ; le rappel, lui, n'a pas encore d'envoi branché : sa pastille
 * « envoyé » reste à zéro.
 */
export const FACETTES_ENVOI_CONVOCATION = [
  { cle: 'a_envoyer', label: 'Convocation à envoyer', classe: 'facette-ambre' },
  { cle: 'envoye', label: 'Convocation envoyée', classe: 'facette-vert' },
];

export const FACETTES_ENVOI_RAPPEL = [
  // Lu dans la trace du planificateur : le rappel ne s'envoie plus à la main.
  { cle: 'a_envoyer', label: 'Rappel pas encore parti', classe: 'facette-ambre' },
  { cle: 'envoye', label: 'Rappel parti', classe: 'facette-vert' },
];

/** Étapes 4 et 6 — situation de la séance à laquelle le document se rapporte. */
export const FACETTES_SEANCE = [
  { cle: 'a_venir', label: 'Séance à venir', classe: 'facette-bleu' },
  { cle: 'en_cours', label: 'En cours', classe: 'facette-ambre' },
  { cle: 'passee', label: 'Séance passée', classe: 'facette-gris' },
];

/** Étape 5 — échéance de rappel, d'après les délais configurés. */
export const FACETTES_RAPPEL = [
  // « Échéance atteinte » et non « à envoyer » : la rangée Envoi porte déjà ce
  // libellé, sur une autre dimension. Deux pastilles homonymes dans la même
  // page se confondraient.
  { cle: 'echeance', label: 'Échéance atteinte', classe: 'facette-rouge' },
  { cle: 'pas_encore', label: 'Pas encore dû', classe: 'facette-bleu' },
  { cle: 'passee', label: 'Session passée', classe: 'facette-gris' },
];
